import React, { useReducer, useEffect, useRef } from 'react';
import { HOME_ROOT } from 'config/api';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from 'theme/withStyles';
import { Collapse } from 'react-collapse';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import PlusMinus from 'common/PlusMinus';
import usePlaid from 'hooks/usePlaid';
import plaidActions from 'core/plaid/actions';

import uiActions from 'core/ui/actions';
import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    hidden: {
      display: 'none',
    },
    verifyDepositsLink: {
      ...theme.links.link,
      '&:hover': {
        textDecoration: 'underline',
      },
      background: 'none transparent',
      float: 'right',
      fontSize: theme.typography.p.fontSize,
      padding: 0,
    },
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'accordion':
      return {
        ...state,
        accordion: !state.accordion,
      };
    default:
      return state;
  }
};

const renderPanelBody = (request) => (
  <ul>
    <li>
      <RawHtml>{request.fundingVerificationSubtitle}</RawHtml>
    </li>
  </ul>
);

export const HomeFundingVerificationPanelBare = ({
  classes,
  fetchAccounts,
  openDialog,
  request,
  resources,
  setAppLoading,
}) => {
  const initialState = {
    accordion: false,
  };
  const [panel, dispatch] = useReducer(reducer, initialState);
  const plaidParams = useRef();

  const [plaidRef, runPlaid] = usePlaid({
    onEvent(event) {
      if (event === 'HANDOFF') {
        const { metadata, publicToken } = plaidParams.current;
        if (publicToken) {
          fetchAccounts(metadata, undefined, request.id);
        }
      }
    },
    onSuccess(token, metadata) {
      if (metadata.accounts[0].verification_status === 'manually_verified') {
        plaidParams.current = { metadata, publicToken: token };
      }
      localStorage.removeItem('plaidLinkToken');
    },
    plaidLinkTokenEndPoint: `${HOME_ROOT}/${request.id}/getPlaidConfigs`,
    setAppLoading,
  });

  useEffect(() => {
    if (plaidRef) {
      plaidRef.open();
    }
  }, [plaidRef]);

  return (
    <div
      className={classnames('panel panel-primary loading panel-funding-verification', classes.root)}
      data-funding-verification-id={request.fundingVerificationId}
      role="tab"
    >
      <div className="panel-heading" id={`#collapseAreaID-funding-verification-${request.id}`}>
        <div
          role="button"
          tabIndex="0"
          aria-expanded={panel.accordion}
          aria-controls={`collapseAreaID-funding-verification-${request.id}`}
          className={classnames('panel-title', classes.panelTitle)}
          onClick={(e) => {
            e.preventDefault();
            dispatch({
              type: 'accordion',
            });
          }}
          onKeyUp={(e) =>
            (e.keyCode === 13 || e.keyCode === 32) &&
            dispatch({
              type: 'accordion',
            })
          }
        >
          <div
            data-toggle="collapse"
            href={`#collapseAreaID-funding-verification-${request.id}`}
            className={classnames('buttonLink get-funding-verification', classes.buttonLink, {
              collapsed: !panel.accordion,
              [classes.collapsed]: !panel.accordion,
            })}
          >
            <PlusMinus collapsed={!panel.accordion} />{' '}
            <RawHtml type="span">{resources.fundingVerificationTitle}</RawHtml>
            <button
              type="button"
              className={classnames('verify-deposits-link', classes.verifyDepositsLink)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                localStorage.removeItem('plaidLinkToken');
                if (request.plaidFunding) {
                  if (plaidRef) {
                    plaidRef.open();
                  } else {
                    runPlaid();
                  }
                } else {
                  openDialog('home_verify_deposits', {
                    request,
                  });
                }
              }}
            >
              {resources.verifyDepositsLinkLabel}{' '}
              <i
                className={classnames('icon-arrow-thin-right', classes.iconArrowThinRight)}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
      <Collapse
        isOpened={panel.accordion}
        id={`collapseAreaID-funding-verification-${request.id}`}
        className="panel-collapse"
      >
        <div
          className={classnames('panel-body', classes.panelBody, {
            [classes.hidden]: !panel.accordion,
          })}
        >
          {renderPanelBody(request)}
        </div>
      </Collapse>
    </div>
  );
};

HomeFundingVerificationPanelBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  fetchAccounts: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  request: PropTypes.shape({
    fundingVerificationId: PropTypes.string,
    id: PropTypes.number,
    plaidFunding: PropTypes.bool,
  }).isRequired,
  resources: PropTypes.shape({
    fundingVerificationTitle: PropTypes.string,
    verifyDepositsLinkLabel: PropTypes.string,
  }).isRequired,
  setAppLoading: PropTypes.func.isRequired,
};

const HomeFundingVerificationPanelStyled = withStyles(styles)(HomeFundingVerificationPanelBare);

export default connect(undefined, {
  fetchAccounts: plaidActions.fetchAccounts,
  setAppLoading: uiActions.setAppLoading,
})(HomeFundingVerificationPanelStyled);
